import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { productivityAPI } from '@/services/productivity';
import { 
  BarChart2, Activity, Users, Clock, 
  CheckCircle2, TrendingUp, Award, Target 
} from 'lucide-react';
import { cn } from '@/lib/utils';

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [personalData, setPersonalData] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load personal stats
        const personalResponse = await productivityAPI.getWeeklyStats(user.id);
        console.log('Personal Response:', personalResponse);
        
        if (personalResponse.success) {
          setPersonalData(personalResponse.data.dailyData || []);
          console.log('Daily Data:', personalResponse.data.dailyData);
        }

        // Load team stats if Admin/Manager
        if (user.role === 'Admin' || user.role === 'Manager') {
          const teamResponse = await productivityAPI.getTeamAnalytics();
          console.log('Team Response:', teamResponse);
          
          if (teamResponse.success) {
            setTeamData(teamResponse.data);
          }
        }
      } catch (error) {
        console.error('Failed to load analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user.id, user.role]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-orange"></div>
      </div>
    );
  }

  // ✅ Handle empty data with better fallback
  const maxFocus = personalData.length > 0 
    ? Math.max(...personalData.map(d => d.focusMinutes || 0), 10) // ✅ Changed from 1 to 10
    : 10;

  console.log('Max Focus:', maxFocus);
  console.log('Personal Data:', personalData);

  // Personal Bar Chart
  const PersonalBarChart = () => {
    if (personalData.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-stone-400">
          No data available
        </div>
      );
    }

    return (
      <div className="flex items-end justify-between h-64 gap-2 pt-8">
        {personalData.map((day, i) => {
          const focusMinutes = day.focusMinutes || 0;
          const barHeight = (focusMinutes / maxFocus) * 100;
          
          console.log(`${day.dayName}: ${focusMinutes}min, height: ${barHeight}%`);
          
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-2 group relative">
              <div className="absolute -top-8 bg-navy-steal text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {focusMinutes} min
              </div>
              <div 
                className="w-full bg-blue-500 rounded-t-sm hover:bg-blue-600 transition-all duration-500"
                style={{ 
                  height: `${Math.max(barHeight, 2)}%`, // ✅ Ensure minimum 2% visibility
                  minHeight: focusMinutes > 0 ? '8px' : '4px' // ✅ Better min height
                }}
              />
              <span className="text-xs text-stone-500 font-medium truncate w-full text-center">
                {day.dayName}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const totalTasks = personalData.reduce((acc, d) => acc + (d.completedTasks || 0), 0);
  const totalSessions = personalData.reduce((acc, d) => acc + (d.sessionsCount || 0), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Helmet><title>Analytics - Productivity</title></Helmet>
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy-steal flex items-center gap-2">
            <Activity className="w-8 h-8 text-safety-orange" />
            Productivity Analytics
          </h1>
          <p className="text-stone-500">Deep dive into performance metrics</p>
        </div>

        {(user.role === 'Admin' || user.role === 'Manager') && teamData && (
          <div className="flex gap-2 bg-stone-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('personal')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                activeTab === 'personal' 
                  ? "bg-white text-navy-steal shadow-sm" 
                  : "text-stone-600 hover:text-navy-steal"
              )}
            >
              My Stats
            </button>
            <button
              onClick={() => setActiveTab('team')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                activeTab === 'team' 
                  ? "bg-white text-navy-steal shadow-sm" 
                  : "text-stone-600 hover:text-navy-steal"
              )}
            >
              Team Analytics
            </button>
          </div>
        )}
      </div>

      {/* ✅ Personal View */}
      {activeTab === 'personal' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                 <BarChart2 className="w-5 h-5 text-blue-500" />
                 Daily Focus Time (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PersonalBarChart />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Efficiency Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent className="h-[300px] flex items-center justify-center">
               <div className="relative w-48 h-48">
                  <svg width="100%" height="100%" viewBox="0 0 42 42" className="transform -rotate-90">
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#f1f5f9" strokeWidth="5"></circle>
                    <circle 
                      cx="21" cy="21" r="15.915" fill="transparent" stroke="#10b981" strokeWidth="5"
                      strokeDasharray={`${totalSessions > 0 ? 75 : 0} 25`} 
                      strokeDashoffset="25"
                    ></circle>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                     <span className="text-3xl font-bold text-navy-steal">{totalTasks}</span>
                     <span className="text-xs text-stone-500">Tasks Done</span>
                  </div>
               </div>
               
               <div className="ml-8 space-y-4">
                  <div className="flex items-center gap-2">
                     <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                     <span className="text-sm text-stone-600">Completed Sessions ({totalSessions})</span>
                  </div>
                  <div className="flex items-center gap-2">
                     <div className="w-3 h-3 bg-slate-200 rounded-full" />
                     <span className="text-sm text-stone-600">Remaining Potential</span>
                  </div>
               </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ✅ Team View (COMPLETE CODE) */}
      {activeTab === 'team' && teamData && (
        <div className="space-y-6">
          {/* Team Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-500">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 uppercase font-bold">Active Users</p>
                  <h3 className="text-2xl font-bold text-navy-steal">{teamData.summary.totalUsers}</h3>
                </div>
                <div className="bg-blue-100 p-2 rounded-full">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-l-purple-500">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 uppercase font-bold">Total Focus Time</p>
                  <h3 className="text-2xl font-bold text-navy-steal">
                    {Math.floor(teamData.summary.totalFocusTime / 60)}
                    <span className="text-sm font-normal text-stone-400">h</span>
                  </h3>
                </div>
                <div className="bg-purple-100 p-2 rounded-full">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-white border-l-4 border-l-green-500">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 uppercase font-bold">Tasks Completed</p>
                  <h3 className="text-2xl font-bold text-navy-steal">{teamData.summary.totalTasksCompleted}</h3>
                </div>
                <div className="bg-green-100 p-2 rounded-full">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-white border-l-4 border-l-orange-500">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 uppercase font-bold">Total Sessions</p>
                  <h3 className="text-2xl font-bold text-navy-steal">{teamData.summary.totalSessions}</h3>
                </div>
                <div className="bg-orange-100 p-2 rounded-full">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Team Members Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Team Performance (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teamData.users.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-stone-400">
                  <p>No team members found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b-2 border-stone-200">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-stone-600">Employee</th>
                        <th className="text-center p-3 text-sm font-semibold text-stone-600">Role</th>
                        <th className="text-center p-3 text-sm font-semibold text-stone-600">Focus Time</th>
                        <th className="text-center p-3 text-sm font-semibold text-stone-600">Completed</th>
                        <th className="text-center p-3 text-sm font-semibold text-stone-600">Pending</th>
                        <th className="text-center p-3 text-sm font-semibold text-stone-600">Sessions</th>
                        <th className="text-center p-3 text-sm font-semibold text-stone-600">Efficiency</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {teamData.users.map((member, idx) => (
                        <tr 
                          key={member.id} 
                          className={cn(
                            "hover:bg-slate-50 transition-colors",
                            idx < 3 && "bg-yellow-50/30"
                          )}
                        >
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {idx < 3 && (
                                <div className={cn(
                                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                  idx === 0 && "bg-yellow-400 text-yellow-900",
                                  idx === 1 && "bg-slate-300 text-slate-700",
                                  idx === 2 && "bg-orange-400 text-orange-900"
                                )}>
                                  {idx + 1}
                                </div>
                              )}
                              {member.avatar ? (
                                <img 
                                  src={member.avatar} 
                                  alt={member.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                                  {member.name.charAt(0)}
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-navy-steal text-sm">{member.name}</p>
                                <p className="text-xs text-stone-500">{member.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="text-xs">
                              {member.role}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold text-purple-600">
                              {member.focusHours}h {member.focusRemainingMinutes}m
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold text-green-600">{member.completedTasks}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold text-orange-600">{member.pendingTasks}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold text-blue-600">{member.totalSessions}</span>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-stone-200 rounded-full h-2">
                                <div 
                                  className={cn(
                                    "h-2 rounded-full transition-all",
                                    member.efficiency >= 70 ? "bg-green-500" : 
                                    member.efficiency >= 40 ? "bg-yellow-500" : "bg-red-500"
                                  )}
                                  style={{ width: `${member.efficiency}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold text-stone-700">
                                {member.efficiency}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
